import React, { useReducer, useState, useEffect } from 'react';
import {
  DatePicker, Label, Tooltip, TooltipButton, IconInfo,
}
  from '@americanexpress/dls-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { validateDateSelector } from '../../Validations/Validations';
import { parseHtml } from '../../utils/util';
import { performOperations } from '../../Operations/Operations';

const getVal = require('lodash/get');

const DateSelector = ({
  cssProperties,
  fieldId,
  fieldRank,
  fieldLabel,
  fieldValidation,
  validations = [],
  dateFormat = null,
  formSubmissionPayload,
  setFormSubmissionPayload,
  datePlaceHolder,
  maxDate,
  setValidationErrors,
  validationErrors,
  setFocus,
  entityToEdit,
  idsToClear,
  comments,
  setComments,
  curPageValues,
  setCurPageValues,
  locale = null,
  selectKey,
  preFilledWith,
  isPreFilled,
  changePageValues,
  setChangePageValues,
  isChangePageValue = false,
  dispatchIdsState,
  dataObject,
  operationsToPerform,
}) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [validationErrState, dispatchErrState] = useReducer((prevState, payload) => ({ ...prevState, ...payload }), { validationErr: '', isValid: true });

  useEffect(() => {
    if (formSubmissionPayload && formSubmissionPayload[fieldId]) {
      setSelectedDate(formSubmissionPayload[fieldId]);
      curPageValues[fieldId] = formSubmissionPayload[fieldId];
    } else if (fieldValidation && fieldValidation.defaultDateToday) {
      const currentDate = new Date();
      const todaysDate = currentDate && dateFormat
        ? moment(currentDate).format(dateFormat)
        : currentDate.toISOString().split('T', 1)[0];
      setSelectedDate(todaysDate);
      curPageValues[fieldId] = todaysDate;
    } else {
      setSelectedDate('');
      curPageValues[fieldId] = '';
    }
    setCurPageValues({ ...curPageValues });
  }, [fieldId]);

  useEffect(() => {
    if (isPreFilled && preFilledWith === 'filledFormData') {
      let preFilledVal = '';
      if (getVal(formSubmissionPayload, selectKey)) {
        preFilledVal = getVal(formSubmissionPayload, selectKey);
      }

      if (locale) {
        preFilledVal = new Date(preFilledVal).toLocaleDateString(locale);
      }
      setSelectedDate(preFilledVal);
    }
  }, [formSubmissionPayload, fieldId]);

  useEffect(() => {
    if (isChangePageValue && fieldId in changePageValues) {
      setSelectedDate(getVal(changePageValues, fieldId));
    }
  }, [changePageValues, isChangePageValue]);

  const handleDatePicker = (event, date) => {
    if (comments && comments[fieldId]) {
      delete comments[fieldId];
      setComments({ ...comments });
    }
    if (validations.length > 0) {
      event.preventDefault();
      const invalidMsg = validateDateSelector(validations, date, { dateFormat, dataObject });
      if (invalidMsg) {
        dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
      } else {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }
    if (date) {
      setSelectedDate(date.dateString);
      setCurPageValues({ ...curPageValues, [fieldId]: date.dateString });
      if (isChangePageValue) {
        changePageValues[fieldId] = date.dateString;
        setChangePageValues({ ...changePageValues });
      }
      if (operationsToPerform?.onChange?.length > 0) {
        performOperations({
          value: date.dateString,
          operations: operationsToPerform.onChange,
          dataObject: { formSubmissionPayload, curPageValues },
          functionObject: { setFormSubmissionPayload },
        });
      }
    }
  };

  const handleOnInvalid = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      handleDatePicker(e, null);
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  };

  const maxDateProvider = (type) => {
    switch (type) {
      case 'today':
        return new Date().toISOString().split('T', 1)[0];
      case 'localebasedtoday':
        return new Date().toLocaleString({ timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).split(',')[0];
      default:
        return null;
    }
  };

  useEffect(() => {
    // validationErr also can change multiple times, so need one isValid
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (entityToEdit && entityToEdit[fieldId]) {
      setSelectedDate(entityToEdit[fieldId]);
      if (isChangePageValue) {
        changePageValues[fieldId] = entityToEdit[fieldId];
        setChangePageValues({ ...changePageValues });
      }
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setSelectedDate('');
      changePageValues[fieldId] = '';
      setChangePageValues({ ...changePageValues });
      delete curPageValues[fieldId];
      setCurPageValues({ ...curPageValues });
      delete formSubmissionPayload[fieldId];
      setFormSubmissionPayload({ ...formSubmissionPayload });
      delete idsToClear.ids[fieldId];
      dispatchIdsState({ toggleState: idsToClear.toggleState, ids: idsToClear });
    }
  }, [idsToClear.toggleState]);

  ///  create a error object here itself and check if it's emptyin element.jsx, then only proceed.
  const handleKeyPress = (event) => {
    const validKey = event.key === 'Backspace' || event.key === 'Delete'
    || event.key === 'Tab' || /[\d/-]/.test(event.key);
    if (!validKey) {
      event.preventDefault();
    }

    // auto add slash for YYYY/MM/DD, DD/MM/YYYY format
    if (event.which !== 8) {
      const numChars = event.target.value.length;
      if (dateFormat === 'YYYY/MM/DD' && (numChars === 4 || numChars === 7)) {
        let thisVal = event.target.value;
        thisVal += '/';
        event.target.value = thisVal;
      } else if (dateFormat === 'DD/MM/YYYY' && (numChars === 2 || numChars === 5)) {
        let thisVal = event.target.value;
        thisVal += '/';
        event.target.value = thisVal;
      }
    }
  };
  return (
    <div className={cssProperties.style}>
      <Label
        htmlFor={fieldId}
        id={`${fieldId}-dpd-label`}
        className={fieldLabel.cssProperties.style}
      >
        {parseHtml(fieldLabel.label)}
        {!fieldValidation
          && (
            <span className={fieldLabel.cssProperties.style}> (Optional)</span>
          )}
      </Label>
      {fieldValidation && fieldValidation.isToolTip
        && (
          <>
            <span>{' '}</span>
            <Tooltip id="ToolTip" triggerElement={<TooltipButton icon={<IconInfo size="sm" title="info" />} />}>{fieldValidation.toolTipInfo}</Tooltip>
          </>
        )}
      <DatePicker
        id={fieldId}
        date={selectedDate}
        // type='date'
        aria-labelledby="dpd-label dpicker-messages"
        datesDescribedBy="dpd-label"
        onInvalid={handleOnInvalid}
        placeholder={datePlaceHolder || dateFormat || 'MM/DD/YYYY'}
        format={dateFormat || 'MM/DD/YYYY'}
        onDateChange={handleDatePicker}
        required={(fieldValidation && fieldValidation.required)}
        maxDate={maxDateProvider(maxDate)}
        onKeyDown={handleKeyPress}
        disabled={fieldValidation && fieldValidation.disabled}
        locale={locale || 'en-US'}
      />
      {
        validationErrState.validationErr
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t col-md-12">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{validationErrState.validationErr}</span>
          </div>
        )
      }
      {
        comments && comments[fieldId]
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{comments[fieldId].comment}</span>
          </div>
        )
      }
    </div>
  );
};

DateSelector.propTypes = {
  fieldId: PropTypes.string,
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  dateFormat: PropTypes.string,
  datePlaceHolder: PropTypes.string,
  maxDate: PropTypes.string,
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  locale: PropTypes.string,
  selectKey: PropTypes.string,
  isPreFilled: PropTypes.string,
  preFilledWith: PropTypes.string,
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  isChangePageValue: PropTypes.bool,
  dispatchIdsState: PropTypes.func,
  dataObject: PropTypes.func,
  setFormSubmissionPayload: PropTypes.func,
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
};

export default DateSelector;
